import React, { useState, useEffect } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import { v4 as uuidv4 } from 'uuid'; // Import UUID to generate unique IDs for orders
import { ref, onValue } from 'firebase/database';
import { db } from '../../firebase';
import './AddNewOrderModal.css';

const AddNewOrderModal = ({ show, handleClose, handleAddOrder }) => {
    const [invoice_number, setInvoiceNumber] = useState('');
    const [orderDescription, setOrderDescription] = useState('');
    const [orderStatus, setOrderStatus] = useState('Start');
    const [reference, setReference] = useState('');
    const [products, setProducts] = useState([]); // Array to hold selected products
    const [searchTerm, setSearchTerm] = useState('');
    const [allProducts, setAllProducts] = useState([]); // Array to hold all products from the database
    const [searchResults, setSearchResults] = useState([]); // Array to hold filtered search results
    const user = localStorage.getItem("admin_name") ? localStorage.getItem("admin_name") : localStorage.getItem('admin_email');

    useEffect(() => {
        const fetchProducts = () => {
            const productsRef = ref(db, 'Products/');
            onValue(productsRef, (snapshot) => {
                if (snapshot.val() !== null) {
                    const productsData = Object.entries(snapshot.val())
                        .map(([id, product]) => ({ id, ...product }))
                    // .sort((a, b) => a.name.localeCompare(b.name));
                    setAllProducts(productsData);
                }
            }, (error) => {
                console.error('Error fetching products:', error);
            });
        };
        fetchProducts();
    }, []);

    useEffect(() => {
        const timer = setTimeout(() => {
            if (searchTerm) {
                const filteredResults = allProducts.filter((product) =>
                    (product.name && product.name.toLowerCase().includes(searchTerm)) ||
                    (product.supplier && product.supplier.toLowerCase().includes(searchTerm))
                );
                // console.log(filteredResults)
                setSearchResults(filteredResults);
            } else {
                setSearchResults([]);
            }
        }, 300); // Debounce delay

        return () => clearTimeout(timer); // Clean up timeout on each render
    }, [searchTerm, allProducts]);

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value.toLowerCase());
    };

    const handleAddProduct = (product) => {
        // Ensure the product is not already in the selected products list
        if (products.some((p) => p.id === product.id)) {
            alert('Product already added to the list.');
            return;
        }
        setProducts((prevProducts) => [
            ...prevProducts,
            { ...product, quantity: 1 }, // Add selected product with initial quantity
        ]);
        setSearchTerm('');
        setSearchResults([]);
    };

    const handleRemoveProduct = (index) => {
        setProducts(products.filter((_, i) => i !== index));
    };

    const handleQuantityChange = (index, quantity) => {
        const updatedProducts = products.map((prod, i) =>
            i === index ? { ...prod, quantity } : prod
        );
        setProducts(updatedProducts);
    };

    const handleSubmit = () => {
        if (!invoice_number) {
            alert('Order name is required.');
            return;
        }
        if (products.length === 0) {
            alert('Please add at least one product.');
            return;
        }
        const newOrder = {
            invoice_id: uuidv4(),
            invoice_number: invoice_number,
            reference: reference,
            order_description: orderDescription,
            order_status: orderStatus,
            added_date: new Date().toISOString(), // Add the current date and time
            ordered_by: user,
            products: products.map(prod => ({
                mpn: prod.id,
                name: prod.name,
                quantity: prod.quantity,
            })),
        };
        handleAddOrder(newOrder);
        // Reset all form fields and state
        setInvoiceNumber('');
        setOrderDescription('');
        setOrderStatus('Start');
        setProducts([]);
        setSearchTerm('');
        setSearchResults([]);
        handleClose();
    };

    return (
        <Modal show={show} onHide={handleClose}
            className="anom-modal">
            <Modal.Header closeButton>
                <Modal.Title>Add New Order</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form.Group className="anom-form-group">
                    <Form.Label>Invoice Number</Form.Label>
                    <Form.Control
                        type="text"
                        value={invoice_number}
                        onChange={(e) => setInvoiceNumber(e.target.value)}
                        placeholder="Enter invoice number"
                        className="anom-input"
                        required
                    />
                </Form.Group>
                <Form.Group className="anom-form-group">
                    <Form.Label>Reference</Form.Label>
                    <Form.Control
                        type="text"
                        value={reference}
                        onChange={(e) => setReference(e.target.value)}
                        placeholder="Enter reference"
                        className="anom-input"
                        required
                    />
                </Form.Group>
                <Form.Group className="anom-form-group">
                    <Form.Label>Description</Form.Label>
                    <Form.Control
                        as="textarea"
                        rows={1}
                        value={orderDescription}
                        onChange={(e) => setOrderDescription(e.target.value)}
                        placeholder="Enter order description"
                        className="anom-textarea"
                    />
                </Form.Group>
                <Form.Group className="anom-form-group">
                    <Form.Label>Order Status</Form.Label>
                    <Form.Control
                        as="select"
                        value={orderStatus}
                        onChange={(e) => setOrderStatus(e.target.value)}
                        className="anom-select"
                    >
                        <option value="Start">Start</option>
                        <option value="In Progress">In Progress</option>
                        <option value="Completed">Completed</option>

                    </Form.Control>
                </Form.Group>
                <Form.Group className="anom-form-group">
                    <Form.Label>Search Products</Form.Label>
                    <Form.Control
                        type="text"
                        value={searchTerm}
                        onChange={handleSearchChange}
                        placeholder="Search by name or supplier"
                        className="anom-search-bar"
                    />
                </Form.Group>
                <div className="anom-search-results">
                    {searchResults.map((product) => (
                        <div key={product.id} className="anom-search-result-item">
                            <span>{product.name} (Supplier: {product.supplier})</span>
                            <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => handleAddProduct(product)}
                                className="anom-add-button"
                            >
                                Add
                            </Button>
                        </div>
                    ))}
                </div>
                <div className="anom-selected-products">
                    <h5>Selected Products</h5>
                    {products.map((product, index) => (
                        <div key={product.id} className="anom-selected-product-item">
                            <span>{product.name}</span>
                            <Form.Control
                                type="number"
                                min="1"
                                value={product.quantity}
                                onChange={(e) =>
                                    handleQuantityChange(index, parseInt(e.target.value, 10))
                                }
                                className="anom-quantity-input"
                            />
                            <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => handleRemoveProduct(index)}
                                className="anom-remove-button"
                            >
                                Remove
                            </Button>
                        </div>
                    ))}
                </div>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose} className="anom-close-button">
                    Close
                </Button>
                <Button variant="primary" onClick={handleSubmit} className="anom-submit-button">
                    Submit Order
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default AddNewOrderModal;
